/** Admin · payments config (GET/PUT /admin/payments, owner-only PUT) — masked key, currencies; secrets stay server-side. */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Info } from "../../components/icons.js";
import { apiErrorText } from "./adminUi.js";

interface PaymentsConfig {
  keyId: string; // masked, e.g. rzp_live_••••7Kd2
  currencies: string[];
  configured: boolean;
  hasSecret: boolean;
  updatedAt: string | null;
}

interface PaymentsResponse {
  payments: PaymentsConfig;
}

/**
 * Razorpay's India account settles in INR — createRazorpayOrder() hardcodes currency:"INR" and
 * nothing reads the stored `currencies` list. The screen used to offer INR/USD/EUR toggles, which
 * persisted and displayed but changed nothing about what a client was actually charged. Rather
 * than ship a control that lies, state the settlement currency as the fact it is. Re-introduce a
 * real selector only when a second settlement currency is genuinely wired end to end.
 */
const SETTLEMENT_CURRENCIES = ["INR"];

export function Payments(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-payments"],
    queryFn: () => api.get<PaymentsResponse>("/admin/payments"),
  });

  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [confirmDisable, setConfirmDisable] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.put<PaymentsResponse>("/admin/payments", {
        keyId: keyId.trim() || undefined,
        keySecret: keySecret.trim() || undefined,
        currencies: SETTLEMENT_CURRENCIES,
      }),
    onSuccess: () => {
      setKeyId("");
      setKeySecret("");
      void queryClient.invalidateQueries({ queryKey: ["admin-payments"] });
    },
  });

  const disable = useMutation({
    mutationFn: () =>
      api.put<PaymentsResponse>("/admin/payments", {
        disable: true,
        currencies: SETTLEMENT_CURRENCIES,
      }),
    onSuccess: () => {
      setConfirmDisable(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-payments"] });
    },
  });

  // A test key in production is not a working gateway, it is a hole: Checkout renders with a
  // "Test Mode" ribbon and a test card verifies as captured, so the client gets paid work for
  // free. The API refuses such a key on a deployed environment; surface WHY here so the state
  // isn't a mystery ("Configured" but nothing charges).
  const isTestKey = /^rzp_test/.test(data?.payments.keyId ?? "");

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Payments</h1>
          <div className="sub">Razorpay gateway · the key is write-only and never returned to this console</div>
        </div>
        <span className="pill n">Owner</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading payment config…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load payment config. Please retry in a moment.</span>
        </div>
      )}

      {data && (
        <div className="grid g2" style={{ marginBottom: 16 }}>
          <div className="card">
            <div className="card-h">
              <h3>Current configuration</h3>
              <span className={data.payments.hasSecret ? "pill v" : "pill n"}>
                {data.payments.hasSecret ? "Configured" : "Not configured"}
              </span>
            </div>
            <div className="card-b">
              <div className="krow">
                <span className="k">Key ID</span>
                <span className="v" style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>
                  {data.payments.keyId || "not set"}
                </span>
              </div>
              <div className="krow">
                <span className="k">Secret</span>
                <span className="v">
                  {data.payments.hasSecret ? (
                    <span className="pill v">Stored</span>
                  ) : (
                    <span className="pill r">Missing</span>
                  )}
                </span>
              </div>
              <div className="krow">
                <span className="k">Settlement currency</span>
                <span className="v">INR</span>
              </div>
              <div className="krow">
                <span className="k">Last updated</span>
                <span className="v mut sm">
                  {data.payments.updatedAt ? new Date(data.payments.updatedAt).toLocaleString() : "—"}
                </span>
              </div>
              {isTestKey && (
                <div className="banner crit" style={{ marginTop: 12 }}>
                  <Info />
                  <div>
                    This is a <b>Razorpay TEST key</b>. It is refused on a deployed environment, so card and
                    UPI payments are <b>off</b> — orgs can keep spending the credits they already hold, but
                    buying new credits is blocked. Replace it with a live <code>rzp_live_…</code> key to
                    accept real payments, or disable the gateway.
                  </div>
                </div>
              )}
              <div className="banner info" style={{ marginTop: 12 }}>
                <Info />
                <div>
                  The key ID is <b>masked</b> and the secret is never returned by the API. Rotation and
                  disablement are written to the audit log.
                </div>
              </div>

              {isOwner && data.payments.hasSecret && (
                <div style={{ marginTop: 14 }}>
                  {confirmDisable ? (
                    <div className="banner crit">
                      <Info />
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 10 }}>
                          Disable the gateway? The stored key and secret are <b>deleted</b>, not just switched
                          off — you'll need to paste them again to re-enable. Until then orgs can keep
                          spending the credits they already hold; buying new credits is blocked.
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="btn sm ghost" onClick={() => setConfirmDisable(false)}>
                            Cancel
                          </button>
                          <button
                            className="btn sm"
                            style={{ color: "var(--crit)", borderColor: "var(--crit)" }}
                            disabled={disable.isPending}
                            onClick={() => disable.mutate()}
                          >
                            {disable.isPending ? "Disabling…" : "Yes, disable the gateway"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="btn sm"
                      style={{ color: "var(--crit)", borderColor: "var(--crit)" }}
                      onClick={() => setConfirmDisable(true)}
                    >
                      Disable gateway
                    </button>
                  )}
                  {disable.isError && (
                    <div className="banner crit" style={{ marginTop: 10 }}>
                      <span>{apiErrorText(disable.error, "Couldn't disable the gateway. Please retry.")}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <h3>Update configuration</h3>
              <span className="tag">Owner-only</span>
            </div>
            <div className="card-b">
              <div className="field" style={{ marginBottom: 10 }}>
                <label>
                  New key ID <span className="hint">leave blank to keep current</span>
                </label>
                <input
                  className="input"
                  value={keyId}
                  onChange={(e) => setKeyId(e.target.value)}
                  placeholder={data.payments.keyId || "rzp_live_…"}
                  disabled={!isOwner}
                />
              </div>
              <div className="field" style={{ marginBottom: 12 }}>
                <label>
                  New key secret <span className="hint">write-only · stored server-side</span>
                </label>
                <input
                  className="input"
                  type="password"
                  value={keySecret}
                  onChange={(e) => setKeySecret(e.target.value)}
                  placeholder="••••••••"
                  disabled={!isOwner}
                />
              </div>
              <p className="mut sm" style={{ margin: "0 0 14px" }}>
                Charges settle in <b>INR</b> — that's what the Razorpay India account supports. Use an{" "}
                <code>rzp_live_…</code> key: a <code>rzp_test_…</code> key is refused in production.
              </p>
              {save.isError && (
                <div className="banner crit" style={{ marginBottom: 12 }}>
                  <span>{apiErrorText(save.error, "Couldn't save payment config. Please retry.")}</span>
                </div>
              )}
              {save.isSuccess && (
                <div className="banner info" style={{ marginBottom: 12 }}>
                  <Info />
                  <span>Saved. The change is in the audit log.</span>
                </div>
              )}
              <button
                className="btn primary"
                // A save that carries neither a new id nor a new secret rewrites the row with the
                // values it already holds — a no-op that still stamps the audit log. Require one.
                disabled={!isOwner || save.isPending || (!keyId.trim() && !keySecret.trim())}
                onClick={() => save.mutate()}
              >
                {save.isPending ? "Saving…" : "Save payment config"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="disclaimer">
        <b>Payment credentials live in the platform settings store</b> (Key Vault in prod); the key ID is masked and
        the secret is never revealed in the console.
      </div>
    </section>
  );
}
